import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';
import {
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { AdminCreationFormValue } from './admin-createion.models';
import {
  MdaListResponse,
  MdaOption,
  RoleListResponse,
  RoleOption,
} from '../../../modules/services/utility-service.models';
import { RoleResponse } from '../../../modules/shared/models/general.modal';
import alertify from 'alertifyjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UserData } from '../user-landing/user-landing.modal';
import { CharLength } from '../../../modules/directives/char-length.directive';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-admin-creation-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CharLength, NgSelectModule],
  templateUrl: './admin-creation-modal.component.html',
  styleUrl: './admin-creation-modal.component.scss',
})
export class AdminCreationModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() mode: 'CREATE' | 'UPDATE' = 'CREATE';
  @Input() isSubmitting = false;
  @Input() user: UserData;
  @Output() close = new EventEmitter<void>();
  @Output() createUser = new EventEmitter<AdminCreationFormValue>();
  @Output() updateUser = new EventEmitter<AdminCreationFormValue>();

  roleOptions: RoleOption[] = [];
  selectedRoleOptions: RoleOption[] = [];

  mdaOptions: MdaOption[] = [];

  readonly userForm: FormGroup;
  private readonly hostElement: HTMLElement;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly apiService: ApiServiceService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.hostElement = this.elementRef.nativeElement;

    this.userForm = this.formBuilder.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.pattern(/^[A-Za-z\s'-]+$/),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.pattern(/^[A-Za-z\s'-]+$/),
        ],
      ],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern(/^\d{11,15}$/)],
      ],
      asin: ['', [Validators.pattern(/^\d{9,11}$/)]],
      roles: [[], Validators.required],
      mda: ['', Validators.required],
    });

    // this.userForm.controls['role'].disable();
  }

  ngOnInit(): void {
    this.getRoles();
    this.getMdas();

    if (this.mode === "UPDATE" && this.user) {
      const [firstName, lastName] = this.user.full_name.split(' ');
      this.userForm.patchValue({
        firstName: firstName || '',
        lastName: lastName || '',
        email: this.user.email || '',
        phoneNumber: this.user.phone_number || '',
        mda: this.user.mda_id || '',
      });
    };
  }

  ngAfterViewInit(): void {
    this.renderer.appendChild(this.document.body, this.hostElement);
  }

  ngOnDestroy(): void {
    if (this.hostElement.parentNode === this.document.body) {
      this.renderer.removeChild(this.document.body, this.hostElement);
    }
  }


  getRoles(): void {
    const data = {
      page: 1,
      pageSize: 100,
    };
    this.apiService.post('/roles/search', data).subscribe({
      next: (response: RoleResponse) => {
        this.roleOptions = response.data.Content ?? [];

        if (this.mode === "UPDATE" && this.user) {
          const userRoleIds = this.user.roles?.map(role => role.id);
  
          if (userRoleIds && userRoleIds.length > 0) {
            const userRoleIdsSet = new Set(userRoleIds);
            const selectedRoleIds = this.roleOptions.filter(
              (role) => userRoleIdsSet.has(role.id),
            ).map(role => role.id);
            this.userForm.patchValue({ roles: selectedRoleIds });
          }
        }
      },
      error: (error) => {
        this.roleOptions = [];
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message || 'Failed to get roles');
        } else {
          alertify.error(JSON.stringify(error) ?? 'Failed to get roles');
        }
      },
    });
  }

  getMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];

        if (this.mode === 'UPDATE' && this.user) {
          const selectedMda = this.mdaOptions.find(
            (mda) => mda.id === this.user?.mda_id,
          );
          this.userForm.patchValue({ mda: selectedMda?.id });
        }
      },
      error: () => {
        this.mdaOptions = [];
      },
    });
  }

  get firstNameControl() {
    return this.userForm.controls['firstName'];
  }

  get lastNameControl() {
    return this.userForm.controls['lastName'];
  }

  get emailControl() {
    return this.userForm.controls['email'];
  }

  get phoneNumberControl() {
    return this.userForm.controls['phoneNumber'];
  }

  get asinControl() {
    return this.userForm.controls['asin'];
  }

  get rolesControl() {
    return this.userForm.controls['roles'];
  }

  get mdaControl() {
    return this.userForm.controls['mda'];
  }

  get canSubmit(): boolean {
    return this.userForm.valid && !this.isSubmitting;
  }

  hasError(controlName: keyof typeof this.userForm.controls): boolean {
    const control = this.userForm.controls[controlName];
    return control.invalid && (control.touched || control.dirty);
  }

  getFirstNameError(): string {
    if (this.firstNameControl.hasError('required')) {
      return 'First name is required.';
    }
    if (this.firstNameControl.hasError('minlength')) {
      return 'First name must be at least 2 characters.';
    }
    if (this.firstNameControl.hasError('pattern')) {
      return 'First name can only contain letters, spaces, apostrophes, or hyphens.';
    }
    return '';
  }

  getLastNameError(): string {
    if (this.lastNameControl.hasError('required')) {
      return 'Last name is required.';
    }
    if (this.lastNameControl.hasError('minlength')) {
      return 'Last name must be at least 2 characters.';
    }
    if (this.lastNameControl.hasError('pattern')) {
      return 'Last name can only contain letters, spaces, apostrophes, or hyphens.';
    }
    return '';
  }

  getEmailError(): string {
    if (this.emailControl.hasError('required')) {
      return 'Email address is required.';
    }
    if (this.emailControl.hasError('email')) {
      return 'Enter a valid email address.';
    }
    return '';
  }

  getPhoneNumberError(): string {
    if (this.phoneNumberControl.hasError('required')) {
      return 'Phone number is required.';
    }
    if (this.phoneNumberControl.hasError('pattern')) {
      return 'Phone number must be 11 to 15 digits.';
    }
    return '';
  }

  getAsinError(): string {
    if (this.asinControl.hasError('pattern')) {
      return 'ASIN must be 9 to 11 digits.';
    }
    return '';
  }

  getRolesError(): string {
    if (this.rolesControl.hasError('required')) {
      return 'Select a roles.';
    }
    return '';
  }

  getMdaError(): string {
    if (this.mdaControl.hasError('required')) {
      return 'Select an MDA.';
    }
    return '';
  }

  closeModal(): void {
    this.close.emit();
  }

  submit(): void {
    if (!this.canSubmit) {
      this.userForm.markAllAsTouched();
      return;
    }

    switch (this.mode) {
      case 'CREATE':
        this.createUser.emit(this.userForm.getRawValue());
        break;
      case 'UPDATE':
        console.log(this.userForm.getRawValue());
        this.updateUser.emit(this.userForm.getRawValue());
        break;
      default:
        throw new Error('Unsupported mode was provided');
    }
  }
}
